import { useEffect, useMemo, useRef, useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import { Abschnitt } from "../bausteine/Grundelemente";
import Fassungshinweis from "../bausteine/Fassungshinweis";
import { Schloss, Textmarker } from "../bausteine/Symbole";
import { nameVon, sichtbareAnnotationen } from "../hilfen/auswertung";
import { useChat } from "../zustand/ChatZustand";
import { auswahlAufheben, leseAuswahl, springeZuAnnotation } from "../hilfen/textauswahl";
import { kurzesDatum } from "../hilfen/datum";

/*
  Die Vorlage im Reiter „Vorlage“.

  Früher war das eine eigene Ansicht, zu der man hin- und von der man
  zurücknavigierte. Jetzt liegt sie im Sitzungsraum neben Notizen und
  Meinungsbild. Zwei Wege führen hinaus und wieder herein, und beide laufen
  über den Reiterwechsel statt über einen Ansichtswechsel:

    aufMarkierung — eine vorhandene Markierung wurde angetippt, samt ihrer
                    Position auf dem Bildschirm. Der Sitzungsraum fragt dann
                    dort, wohin die Stelle soll.
    springeZu     — kommt aus den Notizen zurück und scrollt zur Textstelle
*/
export default function Vorlagenblatt({
  dateiId,
  springeZu,
  aufFassung,
  aufLeiste,
  modus = null,
  sichtbarkeit = "fraktion",
  farbe = "gelb",
  aufMarkierung,
}) {
  const { stammdaten, zustand, ich, aktionen } = useDemo();
  const chat = useChat();
  const datei = stammdaten.dateien[dateiId];
  const vorlage = datei ? stammdaten.vorlagen[datei.vorlageId] : null;

  /*
    `fehler` statt `auswahl`: Eine brauchbare Auswahl wird sofort verarbeitet
    und muss nicht zwischengelagert werden. Nur wenn sie unbrauchbar ist —
    über zwei Absätze hinweg oder zu kurz —, muss die Anwendung etwas sagen.
  */
  const [fehler, setzeFehler] = useState(null);
  const blattRef = useRef(null);

  /* Beim Öffnen gilt die Vorlage als gelesen — die Startseite ändert sich dadurch. */
  useEffect(() => {
    if (dateiId) aktionen.markiereGelesen(dateiId);
  }, [dateiId, aktionen]);

  /*
    Rücksprung aus den Notizen zur zitierten Textstelle.

    Heikel, weil der Reiter im selben Augenblick wechselt: Im Moment, in dem
    dieser Effekt läuft, kann der Zielabsatz noch gar nicht im Dokument stehen.
    Deshalb wird es mehrfach versucht und aufgehört, sobald es geklappt hat.
  */
  useEffect(() => {
    if (!springeZu) return undefined;

    let versuche = 0;
    const takt = window.setInterval(() => {
      versuche += 1;
      if (springeZuAnnotation(springeZu) || versuche > 20) window.clearInterval(takt);
    }, 60);

    return () => window.clearInterval(takt);
  }, [springeZu, dateiId]);

  /*
    Die Markierungsleiste legt sich unten über die volle Breite. Der
    Werkzeugkreis sitzt genau dort. Wer oben Bescheid weiß, kann ihn
    wegnehmen, solange die Leiste steht.
  */
  useEffect(() => {
    if (aufLeiste) aufLeiste(Boolean(fehler));
  }, [fehler, aufLeiste]);

  useEffect(() => () => aufLeiste && aufLeiste(false), [aufLeiste]);

  const absaetzeNachId = useMemo(() => {
    const karte = {};
    if (datei) datei.absaetze.forEach((a) => (karte[a.id] = a));
    return karte;
  }, [datei]);

  const annotationen = datei ? sichtbareAnnotationen(zustand, ich.id, datei.id) : [];

  /*
    Welche Markierungen werden bereits besprochen? Sie bekommen im Text eine
    durchgezogene Unterstreichung.

    Die Beiträge kommen von Matrix, nicht aus dem Browserspeicher. Beim Umzug
    des Chats zeigte diese Zeile eine Weile ins Leere — die Unterstreichung
    blieb dadurch stumm aus, ohne dass irgendwo ein Fehler erschien.
  */
  const zitierteIds = useMemo(
    () => new Set(chat.nachrichten.filter((n) => n.annotationId).map((n) => n.annotationId)),
    [chat.nachrichten]
  );

  if (!datei || !vorlage) {
    return (
      <div className="karte" style={{ marginTop: "1.5rem" }}>
        <p>Diese Vorlage wurde nicht gefunden.</p>
      </div>
    );
  }

  /*
    Der Kern der Bedienung: Eine Textauswahl bedeutet für sich genommen gar
    nichts. Erst wenn oben ein Werkzeug an ist, wird daraus eine Markierung.

    Vorher sprang bei jedem Streichen über den Text ein Kasten auf — man
    konnte nicht einmal etwas kopieren, ohne gefragt zu werden, ob das für die
    Fraktion oder nur für einen selbst sei.
  */
  function pruefeAuswahl() {
    if (!modus) return;

    const ergebnis = leseAuswahl(absaetzeNachId);
    if (!ergebnis) return;

    if (ergebnis.fehler) {
      setzeFehler(ergebnis.fehler);
      return;
    }

    setzeFehler(null);

    aktionen.fuegeAnnotationHinzu({
      dateiId: datei.id,
      absatzId: ergebnis.absatzId,
      start: ergebnis.start,
      ende: ergebnis.ende,
      zitat: ergebnis.zitat,
      sichtbarkeit,
      farbe,
    });

    auswahlAufheben();
  }

  const leisteOffen = Boolean(fehler);
  const seiten = gruppiereNachSeiten(datei.absaetze);

  return (
    <>
      <div className={`dok ${leisteOffen ? "dok--leiste-offen" : ""}`}>
        <div className="dok__kopf">
          <p className="dok__amt">
            {stammdaten.koerperschaft.name} · {vorlage.federfuehrung}
          </p>
          <p className="dok__aktenzeichen">{vorlage.aktenzeichen}</p>
          <h1 className="dok__titel">{vorlage.titel}</h1>

          <div className="dok__zeilen">
            <span>Art</span>
            <span>{vorlage.typ}</span>
            <span>Stand</span>
            <span>{vorlage.status}</span>
            <span>Umfang</span>
            <span>{datei.seitenzahl} Seiten</span>
            <span>Abgerufen</span>
            <span>{kurzesDatum(new Date())} aus dem Ratsinformationssystem</span>
          </div>

          {datei.nichtoeffentlich && (
            <p className="marke-nichtoeffentlich" style={{ marginTop: "var(--luft-3)" }}>
              <Schloss groesse={15} />
              Nichtöffentlich
            </p>
          )}

          <Fassungshinweis vorlage={vorlage} aufFassung={aufFassung} />
        </div>

        <div
          className="dok__blatt"
          ref={blattRef}
          onMouseUp={pruefeAuswahl}
          onTouchEnd={pruefeAuswahl}
        >
          {seiten.map((seite, index) => (
            <div key={seite.nummer}>
              {index > 0 && (
                <p className="dok__seitentrenner">Seite {seite.nummer}</p>
              )}
              {seite.absaetze.map((absatz) => (
                <Absatz
                  key={absatz.id}
                  absatz={absatz}
                  annotationen={annotationen}
                  zitierteIds={zitierteIds}
                  aufKlick={(ids, stelle) => {
                    setzeFehler(null);
                    if (aufMarkierung) aufMarkierung({ ids, stelle });
                  }}
                />
              ))}
            </div>
          ))}

          {datei.anlagenhinweis && (
            <p className="dok__anlagen">{datei.anlagenhinweis}</p>
          )}
        </div>

        <p className="vereinfachung">
          <strong>Hinweis zum Prototyp:</strong> Die Vorlage wird hier als Text dargestellt, nicht
          als PDF. Markierungen kleben deshalb zuverlässig an der Textstelle. Beim späteren Import
          echter PDFs aus dem Ratsinformationssystem ist dieser Punkt der technisch aufwendigste
          Teil — nachzulesen in VEREINFACHUNGEN.md.
        </p>

        <MarkierungsListe annotationen={annotationen} />
      </div>

      {fehler && (
        <Fehlerleiste
          fehler={fehler}
          aufSchliessen={() => {
            auswahlAufheben();
            setzeFehler(null);
          }}
        />
      )}

    </>
  );
}

/* ---------- Absatz mit eingefärbten Markierungen ---------- */

function Absatz({ absatz, annotationen, zitierteIds, aufKlick }) {
  const eigene = annotationen.filter((a) => a.absatzId === absatz.id);

  const inhalt = useMemo(
    () => baueTeile(absatz, eigene, zitierteIds, aufKlick),
    [absatz, eigene, zitierteIds, aufKlick]
  );

  if (absatz.art === "ueberschrift") {
    return (
      <h2 className="dok__ueberschrift" data-absatz-id={absatz.id}>
        {inhalt}
      </h2>
    );
  }

  if (absatz.art === "hinweis") {
    return (
      <p className="dok__hinweiskasten" data-absatz-id={absatz.id}>
        {inhalt}
      </p>
    );
  }

  return (
    <p className="dok__absatz" data-absatz-id={absatz.id}>
      {inhalt}
    </p>
  );
}

/*
  Zerlegt den Absatztext an allen Anfangs- und Endpunkten der Markierungen.
  Überlappende Markierungen werden dadurch sauber dargestellt.
*/
function baueTeile(absatz, annotationen, zitierteIds, aufKlick) {
  if (annotationen.length === 0) return absatz.text;

  const grenzen = new Set([0, absatz.text.length]);
  annotationen.forEach((a) => {
    grenzen.add(Math.max(0, a.start));
    grenzen.add(Math.min(absatz.text.length, a.ende));
  });
  const punkte = [...grenzen].sort((x, y) => x - y);

  const teile = [];
  for (let i = 0; i < punkte.length - 1; i += 1) {
    const von = punkte[i];
    const bis = punkte[i + 1];
    if (bis <= von) continue;

    const text = absatz.text.slice(von, bis);
    const deckend = annotationen.filter((a) => a.start <= von && a.ende >= bis);

    if (deckend.length === 0) {
      teile.push(text);
      continue;
    }

    /*
      Drei Zustände, keiner davon allein über Farbe erkennbar:

        markiert          — nur Schatten
        irgendwohin       — Schatten und durchgezogene Unterstreichung
          geschickt
        nur für mich      — Schatten und gestrichelte Unterstreichung

      Wer eine Vorlage durchblättert, sieht damit auf einen Blick, welche
      Stellen bloß angestrichen sind und welche schon in der Fraktion
      besprochen werden.
    */
    const nurEigene = deckend.every((a) => a.sichtbarkeit === "privat");
    const zitiert = deckend.some((a) => zitierteIds.has(a.id));
    /* Bei mehreren Markierungen übereinander gilt die zuletzt gesetzte. */
    const farbe = deckend[deckend.length - 1].farbe || "gelb";
    const klassen = [
      "markierung",
      `markierung--${farbe}`,
      nurEigene ? "markierung--eigen" : "",
      zitiert ? "markierung--zitiert" : "",
      deckend.length > 1 ? "markierung--mehrfach" : "",
    ]
      .filter(Boolean)
      .join(" ");

    teile.push(
      <mark
        key={`${absatz.id}-${von}`}
        className={klassen}
        data-annotation-ids={deckend.map((a) => a.id).join(" ")}
        tabIndex={0}
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          aufKlick(deckend.map((a) => a.id), rechteckVon(e.currentTarget));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            aufKlick(deckend.map((a) => a.id), rechteckVon(e.currentTarget));
          }
        }}
      >
        {text}
      </mark>
    );
  }
  return teile;
}

/* Wo die Markierung auf dem Bildschirm liegt — dort geht die Frage auf. */
function rechteckVon(element) {
  const r = element.getBoundingClientRect();
  return { oben: r.top, unten: r.bottom, links: r.left, rechts: r.right, breite: r.width };
}

function gruppiereNachSeiten(absaetze) {
  const seiten = [];
  absaetze.forEach((a) => {
    const letzte = seiten[seiten.length - 1];
    if (letzte && letzte.nummer === a.seite) letzte.absaetze.push(a);
    else seiten.push({ nummer: a.seite, absaetze: [a] });
  });
  return seiten;
}

/* ---------- Leiste: die Auswahl war unbrauchbar ---------- */

/*
  Markierungen werden absatzweise gespeichert (siehe VEREINFACHUNGEN.md).
  Reicht die Auswahl über zwei Absätze, ginge die Textstelle verloren. Das
  muss die Anwendung sagen, statt still nichts zu tun.
*/
function Fehlerleiste({ fehler, aufSchliessen }) {
  return (
    <div className="leiste">
      <div className="leiste__inhalt">
        <p>
          {fehler === "mehrere-absaetze"
            ? "Bitte innerhalb eines Absatzes markieren."
            : "Die Auswahl ist zu kurz. Markiere mindestens ein Wort."}
        </p>
        <div className="leiste__knoepfe">
          <button className="knopf knopf--zurueckhaltend" onClick={aufSchliessen}>
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Übersicht aller Markierungen ---------- */

function MarkierungsListe({ annotationen }) {
  const { stammdaten, ich } = useDemo();
  if (annotationen.length === 0) return null;

  const sortiert = [...annotationen].sort((a, b) => a.zeit - b.zeit);

  return (
    <Abschnitt
      titel={`Markierungen · ${annotationen.length}`}
      hinweis="Antippen springt zur Textstelle im Dokument."
    >
      {sortiert.map((a) => (
        <button
          key={a.id}
          className="punkt"
          style={{ alignItems: "flex-start", marginBottom: "var(--luft-2)" }}
          onClick={() => springeZuAnnotation(a.id)}
        >
          <span className="punkt__symbol">
            <Textmarker groesse={18} />
          </span>
          <span className="punkt__text">
            <span style={{ fontFamily: "Georgia, serif" }}>„{kuerze(a.zitat)}“</span>
            <span className="klein leise" style={{ display: "block", marginTop: "0.25rem" }}>
              {nameVon(stammdaten, a.verfasserId, ich.id)}
              {a.sichtbarkeit === "privat" ? " · nur für dich" : ""}
            </span>
          </span>
        </button>
      ))}
    </Abschnitt>
  );
}

function kuerze(text, laenge = 90) {
  return text.length <= laenge ? text : `${text.slice(0, laenge).trimEnd()} …`;
}
